// InvoiceForm.js
import React, { useState } from 'react';
import InvoicePreviewRouter from './InvoicePreviewRouter';

const InvoiceForm = ({ client, project, onBack }) => {
  const [showPreview, setShowPreview] = useState(false);
  const isAliSons = client?.client_name?.includes("Ali & Sons");
  const isEditing = Boolean(project?.editingInvoice);

  const getInitialFormData = () => {
    if (isEditing) {
      const editInvoice = project.editingInvoice;

      // Load saved per-deduction modes ('deduct' | 'add'). Retention Release
      // is ALWAYS 'add' and has no toggle.
      let savedModes = {};
      try {
        savedModes = editInvoice.deduction_modes ? JSON.parse(editInvoice.deduction_modes) : {};
      } catch (e) {
        savedModes = {};
      }

      return {
        header: {
          serialNumber: editInvoice.serial_number || '',
          taxInvoiceRef: editInvoice.tax_invoice_ref || '',
          taxInvoiceDate: editInvoice.tax_invoice_date || new Date().toISOString().split('T')[0],
        },
        details: isAliSons ? {
          billItems: {
            landscape: {
              preliminaries: {
                description: 'Bill No. 01 - Preliminaries & General Requirements',
                amount: editInvoice.landscape_preliminaries || 0,
                unit: 'Item'
              },
              landscapingWorks: {
                description: 'Bill No. 02 - Landscaping Works',
                amount: editInvoice.landscape_works || 0,
                unit: 'Item'
              },
              tenderersAdjustments: {
                description: "Bill No. 03 - Tenderer's Adjustments for Bill No. 02",
                amount: editInvoice.tenderers_adjustments || 0,
                unit: 'Item'
              },
              ProvisionalSums: {
                description: "Bill No. 04 - Provisional Sums",
                amount: editInvoice.provisional_sums || 0,
                unit: 'Item'
              }
            },
            swimming: {
              preliminaries: {
                description: 'Bill No. 01 - Preliminaries General Requirements',
                amount: editInvoice.swimming_preliminaries || 0,
                unit: 'Nos'
              },
              mepWorks: {
                description: 'Bill No. 02 - Swimming Pool MEP Works',
                amount: editInvoice.swimming_mep || 0,
                unit: 'Nos'
              },
              civilWorks: {
                description: 'Bill No. 03 - Swimming Pool Civil & Finishing Works',
                amount: editInvoice.swimming_civil || 0,
                unit: 'Nos'
              }
            }
          },
          totalWDLastPeriod: editInvoice.total_wd_last_period || 0,
          descriptionColor: editInvoice.description_color || 'black',
          customDescription: editInvoice.custom_description || ''
        } : {
          paymentCertificateNo: editInvoice.payment_certificate_no || '',
          certificateDate: editInvoice.certificate_date || new Date().toISOString().split('T')[0],
          cumulativeValue: editInvoice.cumulative_value || '',
          previousPayment: editInvoice.previous_payment || '',
          useCustomDescription: Boolean(editInvoice.custom_description),
          customDescription: editInvoice.custom_description || '',
          customAmount: editInvoice.custom_amount || 0,
          descriptionColor: editInvoice.description_color || 'black',
          totalWDLastPeriod: editInvoice.total_wd_last_period || 0
        },
        deductions: {
          advanceRelease: {
            checked: false,
            percentage: 15,
            fixedAmount: project?.advance_payment_received || 0,
            useFixedAmount: Boolean(project?.advance_payment_received),
            noAmountWarning: !project?.advance_payment_received,
            useStoredAmount: true,
            mode: savedModes.advanceRelease || 'deduct'
          },
          // Retention Release: ALWAYS add (locked, no toggle)
          retentionRelease: {
            checked: Boolean(editInvoice.retention_release_checked),
            percentage: editInvoice.retention_release_checked ? Number(editInvoice.retention_release_percentage) : 10,
            mode: 'add'
          },
          cumulativeretention: {
            checked: Boolean(editInvoice.cumulative_retention_checked),
            percentage: editInvoice.cumulative_retention_checked ? Number(editInvoice.cumulative_retention_percentage) : 10,
            mode: savedModes.cumulativeretention || 'deduct'
          },
          cumulativePerformanceRetention: {
            checked: Boolean(editInvoice.cumulative_performance_retention_checked),
            percentage: editInvoice.cumulative_performance_retention_checked ? Number(editInvoice.cumulative_performance_retention_percentage) : 10,
            mode: savedModes.cumulativePerformanceRetention || 'deduct'
          },
          ncrDeduction: {
            checked: Boolean(editInvoice.ncr_deduction_checked),
            amount: editInvoice.ncr_deduction_checked ? Number(editInvoice.ncr_deduction_amount) : 0,
            mode: savedModes.ncrDeduction || 'deduct'
          },
          otherDeduction: {
            checked: Boolean(editInvoice.other_deduction_checked),
            amount: editInvoice.other_deduction_checked ? Number(editInvoice.other_deduction_amount) : 0,
            includeVat: Boolean(editInvoice.other_deduction_include_vat),
            showSubtotal: Boolean(editInvoice.other_deduction_show_subtotal),
            mode: savedModes.otherDeduction || 'deduct'
          }
        },
        bankDetails: {
          bank: editInvoice.bank_name_branch?.includes('ADCB') ? 'ADCB' : 'INVEST',
          accountName: 'Phoenician Technical Services LLC',
          accountNumber: editInvoice.account_number || (isAliSons ? '2000215166' : '10412895920001'),
          iban: editInvoice.iban || (isAliSons ? 'AE410300000002000215166' : 'AE460030010412895920001'),
          swift: editInvoice.swift || (isAliSons ? 'IBTFAEASXXX' : 'ADCBAEAA')
        }
      };
    }

    // Default values for new invoice
    return {
      header: {
        serialNumber: '',
        taxInvoiceRef: '',
        taxInvoiceDate: new Date().toISOString().split('T')[0],
      },
      details: isAliSons ? {
        billItems: {
          landscape: {
            preliminaries: {
              description: 'Bill No. 01 - Preliminaries & General Requirements',
              amount: 0,
              unit: 'Item'
            },
            landscapingWorks: {
              description: 'Bill No. 02 - Landscaping Works',
              amount: 0,
              unit: 'Item'
            },
            tenderersAdjustments: {
              description: "Bill No. 03 - Tenderer's Adjustments for Bill No. 02",
              amount: 0,
              unit: 'Item'
            },
            ProvisionalSums: {
              description: "Bill No. 04 - Provisional Sums",
              amount: 0,
              unit: 'Item'
            }
          },
          swimming: {
            preliminaries: {
              description: 'Bill No. 01 - Preliminaries General Requirements',
              amount: 0,
              unit: 'Nos'
            },
            mepWorks: {
              description: 'Bill No. 02 - Swimming Pool MEP Works',
              amount: 0,
              unit: 'Nos'
            },
            civilWorks: {
              description: 'Bill No. 03 - Swimming Pool Civil & Finishing Works',
              amount: 0,
              unit: 'Nos'
            }
          }
        },
        totalWDLastPeriod: 0,
        descriptionColor: 'black',
        customDescription: ''
      } : {
        paymentCertificateNo: '',
        certificateDate: new Date().toISOString().split('T')[0],
        cumulativeValue: '',
        previousPayment: '',
        useCustomDescription: false,
        customDescription: '',
        customAmount: 0,
        descriptionColor: 'black',
        totalWDLastPeriod: 0
      },
      deductions: {
        advanceRelease: {
          checked: false,
          percentage: 15,
          fixedAmount: project?.advance_payment_received || 0,
          useFixedAmount: Boolean(project?.advance_payment_received),
          noAmountWarning: !project?.advance_payment_received,
          mode: 'deduct'
        },
        // Retention Release: ALWAYS add (locked, no toggle)
        retentionRelease: { checked: false, percentage: 10, mode: 'add' },
        cumulativeretention: { checked: false, percentage: 10, mode: 'deduct' },
        cumulativePerformanceRetention: { checked: false, percentage: 10, mode: 'deduct' },
        ncrDeduction: { checked: false, amount: 0, mode: 'deduct' },
        otherDeduction: {
          checked: false,
          amount: 0,
          includeVat: false,
          showSubtotal: false,
          mode: 'deduct'
        }
      },
      bankDetails: {
        bank: isAliSons ? 'INVEST' : 'ADCB',
        accountName: 'Phoenician Technical Services LLC',
        accountNumber: isAliSons ? '2000215166' : '10412895920001',
        iban: isAliSons ? 'AE410300000002000215166' : 'AE460030010412895920001',
        swift: isAliSons ? 'IBTFAEASXXX' : 'ADCBAEAA'
      }
    };
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const percentageOptions = Array.from({ length: 100 }, (_, i) => i + 1);

  const handleSubmit = (e) => {
    e.preventDefault();
    setShowPreview(true);
  };

  if (showPreview) {
    return (
      <InvoicePreviewRouter
        formData={formData}
        client={client}
        project={project}
        onBack={() => setShowPreview(false)}
      />
    );
  }

  // Common header section component
  const renderHeader = () => (
    <div className="grid grid-cols-3 gap-4 mb-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">S.N.</label>
        <input
          type="text"
          className="w-full p-2 border rounded"
          value={formData.header.serialNumber}
          onChange={(e) => setFormData({
            ...formData,
            header: { ...formData.header, serialNumber: e.target.value }
          })}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tax Invoice Ref</label>
        <input
          type="text"
          className="w-full p-2 border rounded"
          value={formData.header.taxInvoiceRef}
          onChange={(e) => setFormData({
            ...formData,
            header: { ...formData.header, taxInvoiceRef: e.target.value }
          })}
          required
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Tax Invoice Date</label>
        <input
          type="date"
          className="w-full p-2 border rounded"
          value={formData.header.taxInvoiceDate}
          onChange={(e) => setFormData({
            ...formData,
            header: { ...formData.header, taxInvoiceDate: e.target.value }
          })}
          required
        />
      </div>
    </div>
  );

  // Ali & Sons specific details section
  const renderAliSonsDetails = () => (
    <div className="space-y-6">
      {/* Landscape Works Section */}
      <div className="border p-4 rounded">
        <h3 className="text-lg font-medium mb-4">Landscape Works</h3>
        <div className="space-y-4">
          {Object.entries(formData.details.billItems.landscape).map(([key, value]) => (
            <div key={key} className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {value.description}
                </label>
              </div>
              <div>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  value={value.amount}
                  onChange={(e) => {
                    const newBillItems = {
                      ...formData.details.billItems,
                      landscape: {
                        ...formData.details.billItems.landscape,
                        [key]: {
                          ...value,
                          amount: parseFloat(e.target.value) || 0
                        }
                      }
                    };
                    setFormData({
                      ...formData,
                      details: {
                        ...formData.details,
                        billItems: newBillItems
                      }
                    });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Swimming Pool Works Section */}
      <div className="border p-4 rounded">
        <h3 className="text-lg font-medium mb-4">Swimming Pool Works</h3>
        <div className="space-y-4">
          {Object.entries(formData.details.billItems.swimming).map(([key, value]) => (
            <div key={key} className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {value.description}
                </label>
              </div>
              <div>
                <input
                  type="number"
                  className="w-full p-2 border rounded"
                  value={value.amount}
                  onChange={(e) => {
                    const newBillItems = {
                      ...formData.details.billItems,
                      swimming: {
                        ...formData.details.billItems.swimming,
                        [key]: {
                          ...value,
                          amount: parseFloat(e.target.value) || 0
                        }
                      }
                    };
                    setFormData({
                      ...formData,
                      details: {
                        ...formData.details,
                        billItems: newBillItems
                      }
                    });
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Color Selection for Description */}
      <div className="border p-4 rounded">
        <h3 className="text-lg font-medium mb-4">Description Formatting</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description Text Color
          </label>
          <select
            className="w-full p-2 border rounded"
            value={formData.details.descriptionColor}
            onChange={(e) => setFormData({
              ...formData,
              details: {
                ...formData.details,
                descriptionColor: e.target.value
              }
            })}
          >
            <option value="black">Black</option>
            <option value="blue-600">Blue</option>
            <option value="red-600">Red</option>
            <option value="green-600">Green</option>
          </select>
        </div>
      </div>

      {/* Previous Period Total */}
      <div className="border p-4 rounded">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total WD Last Period
            </label>
            <input
              type="number"
              className="w-full p-2 border rounded"
              value={formData.details.totalWDLastPeriod}
              onChange={(e) => setFormData({
                ...formData,
                details: {
                  ...formData.details,
                  totalWDLastPeriod: parseFloat(e.target.value) || 0
                }
              })}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Standard details section
  const renderStandardDetails = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Payment Certificate No.</label>
          <input
            type="text"
            className="w-full p-2 border rounded"
            value={formData.details.paymentCertificateNo}
            onChange={(e) => setFormData({
              ...formData,
              details: { ...formData.details, paymentCertificateNo: e.target.value }
            })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Certificate Date</label>
          <input
            type="date"
            className="w-full p-2 border rounded"
            value={formData.details.certificateDate}
            onChange={(e) => setFormData({
              ...formData,
              details: { ...formData.details, certificateDate: e.target.value }
            })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cumulative Value</label>
          <input
            type="number"
            className="w-full p-2 border rounded"
            value={formData.details.cumulativeValue}
            onChange={(e) => setFormData({
              ...formData,
              details: { ...formData.details, cumulativeValue: e.target.value }
            })}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Previous Payment</label>
          <input
            type="number"
            className="w-full p-2 border rounded"
            value={formData.details.previousPayment}
            onChange={(e) => setFormData({
              ...formData,
              details: { ...formData.details, previousPayment: e.target.value }
            })}
          />
        </div>
      </div>

      {/* Custom Description Section with Color Picker */}
      <div className="border-t pt-4">
        <div className="flex items-center gap-2 mb-2">
          <input
            type="checkbox"
            className="rounded border-gray-300"
            checked={formData.details.useCustomDescription}
            onChange={(e) => setFormData({
              ...formData,
              details: {
                ...formData.details,
                useCustomDescription: e.target.checked
              }
            })}
          />
          <label className="text-sm font-medium text-gray-700">
            Use Custom Description
          </label>
        </div>

        {formData.details.useCustomDescription && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Description
              </label>
              <textarea
                rows={3}
                className="w-full p-2 border rounded"
                value={formData.details.customDescription}
                onChange={(e) => setFormData({
                  ...formData,
                  details: {
                    ...formData.details,
                    customDescription: e.target.value
                  }
                })}
                placeholder="Enter first part of custom description (date and certificate will be added automatically)"
              />
            </div>

            {/* Description Color Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description Text Color
              </label>
              <select
                className="w-full p-2 border rounded"
                value={formData.details.descriptionColor}
                onChange={(e) => setFormData({
                  ...formData,
                  details: {
                    ...formData.details,
                    descriptionColor: e.target.value
                  }
                })}
              >
                <option value="black">Black</option>
                <option value="blue-600">Blue</option>
                <option value="red-600">Red</option>
                <option value="green-600">Green</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Amount
              </label>
              <input
                type="number"
                className="w-full p-2 border rounded"
                value={formData.details.customAmount || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  details: {
                    ...formData.details,
                    customAmount: parseFloat(e.target.value) || 0
                  }
                })}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Deduct/Add selector for a deduction (NOT shown for Retention Release,
  // which is always Add).
  const renderModeSelect = (key, value) => (
    <select
      className={`w-24 p-1 border rounded ml-1 ${
        value.mode === 'add' ? 'text-green-600 border-green-400 font-medium' : ''
      }`}
      value={value.mode || 'deduct'}
      onChange={(e) => setFormData({
        ...formData,
        deductions: {
          ...formData.deductions,
          [key]: { ...value, mode: e.target.value }
        }
      })}
      disabled={!value.checked}
      title="Deduct subtracts this from the invoice total. Add adds it to the invoice total."
    >
      <option value="deduct">Deduct</option>
      <option value="add">Add</option>
    </select>
  );

  // Common deductions section component
  const renderDeductions = () => (
    <div className="border-t pt-6">
      <h3 className="text-lg font-medium mb-4">Deductions</h3>
      <p className="text-xs text-gray-500 mb-4">
        Choose <span className="text-red-600 font-medium">Deduct</span> or
        <span className="text-green-600 font-medium"> Add</span> per item.
        <span className="ml-1"></span>
      </p>
      <div className="grid grid-cols-2 gap-4">
        {/* Special handling for Advance Release */}
        <div className="flex items-start gap-2">
          <input
            type="checkbox"
            className="rounded border-gray-300 mt-1"
            checked={formData.deductions.advanceRelease.checked}
            onChange={(e) => setFormData({
              ...formData,
              deductions: {
                ...formData.deductions,
                advanceRelease: {
                  ...formData.deductions.advanceRelease,
                  checked: e.target.checked
                }
              }
            })}
          />
          <div className="flex-1">
            <label className="text-sm text-gray-700">
              Advance Release
            </label>

            {/* Show warning if no advance payment amount is available */}
            {formData.deductions.advanceRelease.noAmountWarning && formData.deductions.advanceRelease.checked && (
              <div className="text-amber-600 text-xs mt-1 bg-amber-50 p-1 rounded border border-amber-200">
                No amount found in system. Enter manually or add in system.
              </div>
            )}

            {/* If fixed amount is available, show it with a toggle option */}
            {formData.deductions.advanceRelease.useFixedAmount && formData.deductions.advanceRelease.checked && (
              <div className="mt-1">
                <div className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300"
                    checked={formData.deductions.advanceRelease.useStoredAmount}
                    onChange={(e) => setFormData({
                      ...formData,
                      deductions: {
                        ...formData.deductions,
                        advanceRelease: {
                          ...formData.deductions.advanceRelease,
                          useStoredAmount: e.target.checked
                        }
                      }
                    })}
                  />
                  <label className="text-xs text-green-600">
                    Use stored amount: AED {parseFloat(formData.deductions.advanceRelease.fixedAmount).toLocaleString('en-US', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    })}
                  </label>
                </div>
              </div>
            )}

            {/* Percentage + Deduct/Add mode */}
            <div className="flex items-center mt-1">
              <select
                className="w-20 p-1 border rounded"
                value={formData.deductions.advanceRelease.percentage}
                onChange={(e) => setFormData({
                  ...formData,
                  deductions: {
                    ...formData.deductions,
                    advanceRelease: {
                      ...formData.deductions.advanceRelease,
                      percentage: parseInt(e.target.value)
                    }
                  }
                })}
                disabled={!formData.deductions.advanceRelease.checked}
              >
                {percentageOptions.map(percent => (
                  <option key={percent} value={percent}>
                    {percent}%
                  </option>
                ))}
              </select>
              {renderModeSelect('advanceRelease', formData.deductions.advanceRelease)}
            </div>
          </div>
        </div>

        {/* Render other deductions normally */}
        {Object.entries(formData.deductions)
          .filter(([key]) => key !== 'advanceRelease')
          .map(([key, value]) => {
            const isRetentionRelease = key === 'retentionRelease';
            return (
              <div key={key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  className="rounded border-gray-300"
                  checked={value.checked}
                  onChange={(e) => setFormData({
                    ...formData,
                    deductions: {
                      ...formData.deductions,
                      [key]: { ...value, checked: e.target.checked }
                    }
                  })}
                />
                <label className="text-sm text-gray-700">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                  {isRetentionRelease && (
                    <span className="text-green-600 ml-1"></span>
                  )}
                </label>
                {value.hasOwnProperty('percentage') ? (
                  <select
                    className="w-20 p-1 border rounded"
                    value={value.percentage}
                    onChange={(e) => setFormData({
                      ...formData,
                      deductions: {
                        ...formData.deductions,
                        [key]: { ...value, percentage: parseInt(e.target.value) }
                      }
                    })}
                    disabled={!value.checked}
                  >
                    {percentageOptions.map(percent => (
                      <option key={percent} value={percent}>
                        {percent}%
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      className="w-24 p-1 border rounded"
                      value={value.amount}
                      onChange={(e) => setFormData({
                        ...formData,
                        deductions: {
                          ...formData.deductions,
                          [key]: { ...value, amount: parseFloat(e.target.value) || 0 }
                        }
                      })}
                      disabled={!value.checked}
                    />
                    {key === 'otherDeduction' && value.checked && (
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={value.includeVat}
                            onChange={(e) => setFormData({
                              ...formData,
                              deductions: {
                                ...formData.deductions,
                                otherDeduction: {
                                  ...value,
                                  includeVat: e.target.checked
                                }
                              }
                            })}
                          />
                          <label className="text-sm text-gray-700">Include VAT</label>
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            className="rounded border-gray-300"
                            checked={value.showSubtotal}
                            onChange={(e) => setFormData({
                              ...formData,
                              deductions: {
                                ...formData.deductions,
                                otherDeduction: {
                                  ...value,
                                  showSubtotal: e.target.checked
                                }
                              }
                            })}
                          />
                          <label className="text-sm text-gray-700">Show Subtotal</label>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {/* Deduct / Add mode for this deduction - NOT for Retention Release */}
                {!isRetentionRelease && renderModeSelect(key, value)}
              </div>
            );
          })}
      </div>
    </div>
  );

  // Common bank details section component
  const renderBankDetails = () => (
    <div className="border-t pt-6">
      <label className="block text-sm font-medium text-gray-700 mb-1">Bank</label>
      <select
        className="w-full p-2 border rounded"
        value={formData.bankDetails.bank}
        onChange={(e) => {
          const isADCB = e.target.value === 'ADCB';
          setFormData({
            ...formData,
            bankDetails: {
              bank: e.target.value,
              accountName: 'Phoenician Technical Services LLC',
              accountNumber: isADCB ? '10412895920001' : '2000215166',
              iban: isADCB ? 'AE460030010412895920001' : 'AE410300000002000215166',
              swift: isADCB ? 'ADCBAEAA' : 'IBTFAEASXXX'
            }
          });
        }}
      >
        <option value="ADCB">ABU DHABI COMMERCIAL BANK PJSC</option>
        <option value="INVEST">INVEST BANK PJSC</option>
      </select>
    </div>
  );

  // Main render
  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <button
        onClick={onBack}
        className="mb-6 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
      >
        ← Back to Projects
      </button>

      <form onSubmit={handleSubmit} className="space-y-6">
        {renderHeader()}
        {isAliSons ? renderAliSonsDetails() : renderStandardDetails()}
        {renderDeductions()}
        {renderBankDetails()}

        <div className="flex justify-end space-x-4 pt-6">
          <button
            type="button"
            onClick={onBack}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700"
          >
            Generate Invoice
          </button>
        </div>
      </form>
    </div>
  );
};

export default InvoiceForm;